import React, {Component} from 'react';
import {connect} from 'react-redux';
import {fetchEnd, fetchStart} from 'react-admin';
import {withStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import clubDocumentationParser from "../documentationParser";

// Daily [hourStart, hourEnd[ ranges, ending the next day when hourEnd <= hourStart.
const SHIFTS = [
  {label: 'Journée', hourStart: 8, hourEnd: 8},
  {label: '8h-15h', hourStart: 8, hourEnd: 15},
  {label: '15h-22h', hourStart: 15, hourEnd: 22},
  {label: '22h-8h', hourStart: 22, hourEnd: 8},
];

const styles = theme => {
  return ({
    root: {
      flexGrow: 1,
    },
    paper: {
      padding: theme.spacing.unit * 2,
      color: theme.palette.text.primary,
    },
    textField: {
      marginLeft: theme.spacing.unit * 1,
      marginRight: theme.spacing.unit * 1,
      width: 200,
    },
    list: {
      width: '100%',
      backgroundColor: theme.palette.background.paper,
    },
  });
}

class StatsTable extends Component {
  _isMounted = false;
  _requestId = 0;
  state = {
    data: null,
    error: null,
    api: null,
    isLoading: true,
    shift: 0,
    dateStart: (new Date()).toISOString().slice(0, 10),
    dateEnd: (new Date()).toISOString().slice(0, 10)
  };

  componentDidMount() {
    this._isMounted = true;
    clubDocumentationParser().then(({api}) => {
      if (this._isMounted) {
        this.setState({api});
        this.loadData({dateEnd: this.state.dateEnd, dateStart: this.state.dateEnd, shift: this.state.shift});
      }
    }).catch((e) => {
      console.log(e);
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  changeDateStart(event) {
    this.loadData({dateEnd: this.state.dateEnd, dateStart: event.target.value, shift: this.state.shift});
  }

  changeDateEnd(event) {
    this.loadData({dateStart: this.state.dateStart, dateEnd: event.target.value, shift: this.state.shift});
  }

  changeShift(event, shift) {
    this.loadData({dateStart: this.state.dateStart, dateEnd: this.state.dateEnd, shift});
  }

  loadData({dateStart, dateEnd, shift}) {
    const {
      fetchStart,
      fetchEnd
    } = this.props;
    const {
      entrypoint
    } = this.state.api;
    this.setState({isLoading: true, error: null, dateStart, dateEnd, shift});
    // Only the latest request may update the screen.
    const requestId = ++this._requestId;
    const isCurrent = () => this._isMounted && requestId === this._requestId;

    fetchStart();
    const fetchHeaders = {
      'Authorization': `Bearer ${window.localStorage.getItem('token')}`,
      'X-HOTEL': process.env.REACT_APP_HOTEL
    };
    const entrypointUrl = new URL(entrypoint, window.location.href);
    const {hourStart, hourEnd} = SHIFTS[shift];
    const stats_uri = new URL(`/bookings/stats/1?date_start=${dateStart}&date_end=${dateEnd}&hour_start=${hourStart}&hour_end=${hourEnd}`, entrypointUrl);

    fetch(stats_uri.toString(), {
      headers: new Headers(fetchHeaders)
    })
      .then(response => response.json().catch(() => ({})).then(json => {
        if (!response.ok) {
          throw new Error(json.error || `Erreur ${response.status}`);
        }
        return json;
      }))
      .then((data) => {
        if (isCurrent()) {
          this.setState({isLoading: false, data});
        }
      })
      .catch(error => {
        if (isCurrent()) {
          this.setState({isLoading: false, data: null, error: error.message});
        }
      })
      .finally(() => fetchEnd())
  }

  render() {
    const {classes} = this.props;
    const {isLoading, dateStart, dateEnd, data, error, shift} = this.state;

    const periodChooser = (
      <Paper className={classes.paper}>
        <TextField
          label="Début de période"
          type="date"
          defaultValue={dateStart}
          className={classes.textField}
          onChange={this.changeDateStart.bind(this)}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <strong>au</strong>
        <TextField
          label="Fin de période"
          type="date"
          defaultValue={dateEnd}
          className={classes.textField}
          onChange={this.changeDateEnd.bind(this)}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Paper>
    );

    let stats = null;
    if (isLoading) {
      stats = (
        <Grid item xs={12}>
          <LinearProgress style={{margin: '20px', width: '200px'}}/>
        </Grid>
      );
    } else if (error) {
      stats = (
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <h4>Impossible de charger les statistiques : {error}</h4>
          </Paper>
        </Grid>
      );
    } else {
      const i18 = {
        shop: 'Espèces',
        agency: 'Agence',
        online: 'En ligne (manuel)',
        Stripe: 'En ligne (Stripe)',
        card: 'Carte'
      };
      let total = 0, totalOptions = 0, totalSources = 0, totalRooms = 0;
      stats = (
        <>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <h4>{SHIFTS[shift].label} : statistiques basées sur les réservations encaissées entre
                le {(new Date(data.dateStart.date)).toLocaleString()} et
                le {(new Date(data.dateEnd.date)).toLocaleString()}</h4>
            </Paper>
          </Grid>
          <Grid item sm={2} xs={12}>
            <Paper className={classes.paper}>
              <h4>Encaissements</h4>
              <List className={classes.list}>
                {
                  data.payments.map(item => {
                    total += parseFloat(item.sum);
                    return (
                      <ListItem divider key={item.mode}>
                        <ListItemText primary={i18[item.mode] ? i18[item.mode] : item.mode} secondary={`${item.sum}€`}/>
                      </ListItem>
                    )
                  })
                }
                <ListItem key={"sum"}>
                  <ListItemText primary={"Total"} secondary={`${total}€`}/>
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid item sm={3} xs={12}>
            <Paper className={classes.paper}>
              <h4>Options vendues</h4>
              <List className={classes.list}>
                {
                  data.options.map(item => {
                    totalOptions += parseFloat(item.value);
                    return (
                      <ListItem divider key={item.desc}>
                        <ListItemText primary={item.desc} secondary={`${item.value}€`}/>
                      </ListItem>
                    )
                  })
                }
                <ListItem key={"sum"}>
                  <ListItemText primary={"Total"} secondary={`${totalOptions}€`}/>
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid item sm={3} xs={12}>
            <Paper className={classes.paper}>
              <h4>Chambres vendues</h4>
              <List className={classes.list}>
                {
                  data.rooms.map(item => {
                    totalRooms += parseFloat(item.value);
                    return (
                      <ListItem divider key={item.desc}>
                        <ListItemText primary={item.desc} secondary={`${item.value}€`}/>
                      </ListItem>
                    )
                  })
                }
                <ListItem key={"sum"}>
                  <ListItemText primary={"Total"} secondary={`${totalRooms}€`}/>
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid item sm={4} xs={12}>
            <Paper className={classes.paper}>
              <h4>Sources</h4>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.sources.forEach(item => {
                    if (item.sum) {
                      totalSources += parseFloat(item.sum);
                    }
                  })}
                  {data.sources.map(item => (
                    <TableRow key={item.source}>
                      <TableCell>{item.source ? item.source : 'Inconnue'}</TableCell>
                      <TableCell align="right">{item.total}</TableCell>
                      <TableCell align="right">{item.sum ? item.sum : 0}€</TableCell>
                      <TableCell align="right">{item.sum ? (item.sum * 100 / totalSources).toFixed(2) : 0}%</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2}/>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">{totalSources}€</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </>
      );
    }

    return (
      <div className={classes.root}>
        <Grid container spacing={32}>
          <Grid item xs={12}>
            {periodChooser}
          </Grid>
          <Grid item xs={12}>
            <Paper>
              <Tabs value={shift} onChange={this.changeShift.bind(this)} indicatorColor="primary" textColor="primary">
                {SHIFTS.map(item => <Tab key={item.label} label={item.label}/>)}
              </Tabs>
            </Paper>
          </Grid>
          {stats}
        </Grid>
      </div>
    );
  }
}

const StatsListWrapper = (props) => (
  <div>
    <StatsTable {...props} />
  </div>
);

const mapDispatchToProps = {
  fetchEnd,
  fetchStart
};

export const StatsList = connect(null, mapDispatchToProps)(
  withStyles(styles)(StatsListWrapper)
);
